import React, { useState, useEffect } from 'react';
import { Lock, LogOut, Trophy, Key, Calendar, Edit2, Database, Users, Award, Plus, Trash2, Link, ChevronDown } from 'lucide-react';
import { ConnectionControl } from './ConnectionControl';

interface CourseInfo {
  name: string;
  location: string;
  selectedTee: {
    name: string;
    rating: number;
    slope: number;
    yardage: number;
  } | undefined;
}

interface TournamentHeaderProps extends HeaderBaseProps {
  activeTab: string;
  selectedTournament: Tournament | null;
  setActiveTab: (tab: 'tournaments' | 'scoring' | 'leaderboard' | 'import' | 'matchplay' | 'members' | 'hc-standing' | 'tx-cup') => void;
  tournamentName: string;
  courseName: string;
  date: string;
  isAdmin: boolean;
  isConnected?: boolean;
  onAdminClick: () => void;
  onLogout: () => void;
  onPasswordChange: (oldPassword: string, newPassword: string) => void;
  onDateChange: (startDate: string, endDate: string) => void;
  onTitleChange: (title: string) => void;
  courseInfo?: CourseInfo;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  year?: string;
  onYearChange?: (year: string) => void;
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
}

export function TournamentHeader({ 
  selectedTournament,
  tournamentName, 
  courseName, 
  activeTab,
  date,
  isAdmin,
  isConnected = false,
  onAdminClick,
  onLogout,
  onPasswordChange,
  onDateChange,
  onTitleChange,
  courseInfo,
  setActiveTab,
  searchTerm,
  setSearchTerm,
  year = '2025',
  onYearChange = () => {}
}: TournamentHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(tournamentName);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditMembers, setShowEditMembers] = useState(false);
  const [showDeleteMembers, setShowDeleteMembers] = useState(false);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && !(event.target as Element).closest('.menu-container')) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [searchValue, setSearchValue] = useState('');

  // Sync search value with prop
  useEffect(() => {
    if (searchTerm !== undefined) {
      setSearchValue(searchTerm);
    }
  }, [searchTerm]);

  const handleTitleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onTitleChange(editedTitle);
    setIsEditing(false);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    onPasswordChange(oldPassword, newPassword);
    setShowPasswordChange(false);
    setOldPassword('');
    setNewPassword('');
  };

  // Get dynamic title based on active tab
  const getTitle = () => {
    switch (activeTab) {
      case 'members':
        return 'Members';
      case 'hc-standing':
        return 'Houston Cup Standings';
      case 'tx-cup':
        return (
          <div>
            <div className="text-base sm:text-lg font-medium">2025 TX Cup Standings</div>
            <div className="text-[9px] sm:text-[10px] text-primary-300 leading-[1.1] mt-1">
              <div>*Min 2 tournament rounds</div>
              <div>*Sum of lowest 2 rounds in 2025</div>
              <div>*Top 30 automatically qualify</div>
            </div>
          </div>
        );
      default:
        // For tournament-related tabs, always show tournament name if available
        return selectedTournament?.name || 'Tournament';
    }
  };

  return (
    <header className="bg-primary-800 text-white sticky top-0 z-50 safe-area-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="space-y-2">
          {/* Top row with logo and calendar */}
          <div className="flex items-center justify-between border-b border-primary-700 pb-2 relative">
            <div className="flex items-center gap-3">
              <div className="relative menu-container">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="text-white font-bold text-lg hover:text-primary-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-primary-800 rounded px-2 py-1"
                >
                  HVGA
                </button>
                
                {showMenu && (
                  <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setActiveTab('tournaments'); 
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem"
                      >
                        Tournaments
                      </button>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setActiveTab('members');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem"
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Members Profile
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setActiveTab('tx-cup');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem"
                      >
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4" />
                          TX Cup Standing
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setActiveTab('hc-standing');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem"
                      >
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4" />
                          HC Standing
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <input
                type="date"
                value={date || ''}
                onChange={(e) => onDateChange(e.target.value, e.target.value)}
                className="w-28 sm:w-auto bg-primary-700/50 text-sm text-white border border-primary-600 rounded px-2 py-1 focus:outline-none focus:border-primary-400 min-h-[36px]"
              />
            </div>
            {/* Controls */}
            <div className="flex items-center gap-1 sm:gap-4">
              <ConnectionControl isAdmin={isAdmin} />

              {isAdmin ? (
                <div className="flex items-center gap-1 sm:gap-3">
                  <button
                    onClick={() => setShowPasswordChange(true)}
                    className="p-1.5 text-primary-300 hover:text-white transition-colors"
                  >
                    <Key className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onLogout}
                    className="p-1.5 text-primary-300 hover:text-white transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={onAdminClick}
                  className="p-1.5 text-primary-300 hover:text-white transition-colors"
                >
                  <Lock className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Bottom row with tournament info */}
          <div className="flex items-center justify-between h-12 relative">
            <div className="flex-1 flex items-center justify-between">
              <div>
                {activeTab === 'hc-standing' ? (
                  <h1 className="text-base sm:text-lg font-medium truncate max-w-[220px] sm:max-w-[400px] -translate-y-2">
                    {getTitle()}
                  </h1>
                ) : isEditing ? (
                  <form onSubmit={handleTitleSubmit} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="bg-primary-700/50 text-white text-base sm:text-lg font-medium rounded px-2 py-0.5 border border-primary-600 focus:outline-none focus:border-primary-400"
                      autoFocus
                    />
                    <button
                      type="submit"
                      className="text-primary-300 hover:text-white transition-colors"
                    >
                      Save
                    </button>
                  </form>
                ) : (
                  <div className="flex items-center gap-2">
                    <h1 className="text-base sm:text-lg font-medium truncate max-w-[220px] sm:max-w-[400px]">
                      {getTitle()}
                    </h1>
                    {isAdmin && activeTab === 'tournaments' && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="text-primary-300 hover:text-white transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                {courseInfo && courseInfo.selectedTee && activeTab !== 'members' && activeTab !== 'hc-standing' && activeTab !== 'tx-cup' && (
                  <>
                    <div className="text-[10px] sm:text-xs text-primary-300 truncate flex items-center gap-2">
                      <span className="flex items-center gap-2">
                        <span className="truncate">{courseInfo.name}</span>
                      </span>
                      {selectedTournament?.metadata?.sponsor?.active && selectedTournament.metadata.sponsor.logo_url && activeTab !== 'members' && activeTab !== 'hc-standing' && activeTab !== 'tx-cup' && (
                        <a 
                          href={selectedTournament.metadata.sponsor.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center hover:opacity-80 transition-opacity shrink-0"
                          title={selectedTournament.metadata.sponsor.name}
                        >
                          <img
                            src={selectedTournament.metadata.sponsor.logo_url}
                            alt={`${selectedTournament.metadata.sponsor.name} logo`}
                           className="absolute right-0 top-[56%] -translate-y-[50%] h-16 sm:h-16 w-20 sm:w-28 md:w-32 lg:w-40 object-contain"
                           loading="lazy"
                          />
                        </a>
                      )}
                    </div>
                    {/* Tee info for desktop */}
                    <div className="hidden sm:flex text-[10px] text-primary-400 items-center gap-2">
                      <span>{courseInfo.selectedTee.name}</span>
                      <span>•</span>
                      <span>{courseInfo.selectedTee.yardage} yards</span>
                      <span>•</span>
                      <span>Rating {courseInfo.selectedTee.rating}</span>
                      <span>•</span>
                      <span>Slope {courseInfo.selectedTee.slope}</span>
                    </div>
                    {/* Tee info for mobile */}
                    <div className="sm:hidden text-[10px] text-primary-400 flex flex-wrap gap-x-2">
                      <span>{courseInfo.selectedTee.name}</span>
                      <span>•</span>
                      <span>{courseInfo.selectedTee.yardage}y</span>
                      <span>•</span>
                      <span>R{courseInfo.selectedTee.rating}</span>
                      <span>•</span>
                      <span>S{courseInfo.selectedTee.slope}</span>
                    </div>
                  </>
                )}
                {(activeTab === 'members' || activeTab === 'hc-standing' || activeTab === 'tx-cup') && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[45%]">
                    <input
                      type="text"
                      placeholder="Search members..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-1 text-[10px] sm:text-sm bg-primary-700/50 text-white border border-primary-600 rounded focus:outline-none focus:border-primary-400 placeholder-primary-300"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="w-4"></div>
          </div>
        </div>
        {/* Search bar for members and standings */}
        <div className="relative">
          {activeTab === 'hc-standing' && (
            <div className="absolute left-0 w-[45%] -top-[2.25rem]">
              <select
                value={year}
                onChange={(e) => onYearChange(e.target.value)}
                className="bg-transparent text-white px-0 h-5 text-[10px] sm:text-xs font-medium appearance-none cursor-pointer focus:outline-none focus:ring-0 border-0 w-full"
              >
                {[...Array(4)].map((_, i) => {
                  const yearNum = 2024 + i;
                  return (
                    <option key={yearNum} value={yearNum} 
                      className="text-gray-900">
                      {yearNum} - {yearNum === 2024 ? 'Feb-Jul' : 
                                yearNum >= 2025 ? `Nov '${(yearNum-1).toString().slice(-2)} - May '${yearNum.toString().slice(-2)}` : 
                                'Full Year'}
                    </option>
                  );
                })}
              </select>
              <ChevronDown className="w-2.5 h-2.5 text-primary-300 absolute right-0 top-1/2 transform -translate-y-1/2 pointer-events-none" />
            </div>
          )}
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-gray-900 text-lg font-medium mb-4">Change Admin Password</h3>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              {passwordError && (
                <p className="text-sm text-red-600">{passwordError}</p>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setOldPassword('');
                    setNewPassword('');
                    setPasswordError('');
                  }}
                  className="px-3 py-1.5 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}